import type { Design } from '../types/editor.types';

/** Convert camelCase style keys → kebab-case CSS properties */
const toKebab = (key: string) => key.replace(/([A-Z])/g, '-$1').toLowerCase();

/** Serialize a styles object to an inline CSS string */
const renderStyles = (styles: Record<string, unknown>) =>
  Object.entries(styles || {})
    .filter(([k, v]) => v !== undefined && v !== '' && !['borderTopWidth', 'borderTopStyle', 'borderTopColor'].includes(k))
    .map(([k, v]) => `${toKebab(k)}: ${v}`)
    .join('; ');

/** Render a single design element to HTML string */
const renderElement = (el: {
  type: string;
  content: Record<string, unknown>;
  styles: Record<string, unknown>;
}): string => {
  const styles = el.styles || {};
  const s = renderStyles(styles);
  const align = (styles.textAlign as string) ?? 'left';
  const pad = (styles.padding as string) ?? '0';

  switch (el.type) {
    /* ── HEADING ──────────────────────────── */
    case 'heading': {
      const { text, level } = el.content as { text: string; level: string };
      const tag = level ?? 'h2';
      return `<${tag} style="${s}">${text ?? ''}</${tag}>`;
    }

    /* ── PARAGRAPH ───────────────────────── */
    case 'paragraph': {
      const { text } = el.content as { text: string };
      return `<p style="${s}">${text ?? ''}</p>`;
    }

    /* ── TEXT (rich html) ────────────────── */
    case 'text': {
      const { html } = el.content as { html: string };
      return `<div style="${s}">${html ?? ''}</div>`;
    }

    /* ── BUTTON ──────────────────────────── */
    case 'button': {
      const { text, href, target } = el.content as { text: string; href: string; target: string };
      const bg = (styles.backgroundColor as string) ?? '#6366f1';
      const color = (styles.color as string) ?? '#ffffff';
      const bpad = (styles.padding as string) ?? '12px 24px';
      const radius = (styles.borderRadius as string) ?? '6px';
      const fs = (styles.fontSize as string) ?? '14px';
      const fw = (styles.fontWeight as string) ?? '600';
      return `<div style="text-align:${align};padding:${pad}">
  <a href="${href ?? '#'}" target="${target ?? '_blank'}"
    style="display:inline-block;background-color:${bg};color:${color};
           padding:${bpad};border-radius:${radius};font-size:${fs};
           font-weight:${fw};text-decoration:none">${text ?? 'Click Here'}</a>
</div>`;
    }

    /* ── IMAGE ───────────────────────────── */
    case 'image': {
      const { src, alt, width, href } = el.content as { src: string; alt: string; width: string; href: string };
      const img = `<img src="${src ?? ''}" alt="${alt ?? ''}" width="${width ?? '100%'}" style="max-width:100%;display:inline-block" />`;
      const wrapped = href ? `<a href="${href}" target="_blank">${img}</a>` : img;
      return `<div style="text-align:${align};padding:${pad}">${wrapped}</div>`;
    }

    /* ── DIVIDER ─────────────────────────── */
    case 'divider': {
      const bw = (el.styles.borderTopWidth as string) ?? '1px';
      const bs = (el.styles.borderTopStyle as string) ?? 'solid';
      const bc = (el.styles.borderTopColor as string) ?? '#e2e8f0';
      const margin = (el.styles.margin as string) ?? '16px 0';
      return `<hr style="border:none;border-top:${bw} ${bs} ${bc};margin:${margin}" />`;
    }

    /* ── HTML ────────────────────────────── */
    case 'html': {
      const { code } = el.content as { code: string };
      return code ?? '';
    }

    /* ── VIDEO ───────────────────────────── */
    case 'video': {
      const { src, thumbnail } = el.content as { src: string; thumbnail: string };
      if (thumbnail) {
        return `<div style="text-align:${align};padding:${pad}">
  <a href="${src ?? '#'}" target="_blank" style="display:inline-block;position:relative">
    <img src="${thumbnail}" alt="Video" style="max-width:100%;display:block" />
    <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center">
      <div style="width:56px;height:56px;border-radius:50%;background:rgba(0,0,0,.6);
                  display:flex;align-items:center;justify-content:center">
        <span style="color:white;font-size:24px;margin-left:4px">&#9654;</span>
      </div>
    </div>
  </a>
</div>`;
      }
      return `<div style="text-align:${align};padding:${pad}">
  <a href="${src ?? '#'}" target="_blank" style="color:#6366f1">Watch Video &#9654;</a>
</div>`;
    }

    /* ── TIMER ───────────────────────────── */
    case 'timer': {
      const { endDate, labels } = el.content as {
        endDate: string;
        labels: { days: string; hours: string; minutes: string; seconds: string };
      };
      const lbs = labels ?? { days: 'Days', hours: 'Hours', minutes: 'Minutes', seconds: 'Seconds' };
      const diff = Math.max(0, new Date(endDate ?? Date.now()).getTime() - Date.now());
      const d = String(Math.floor(diff / 86400000)).padStart(2, '0');
      const h = String(Math.floor((diff % 86400000) / 3600000)).padStart(2, '0');
      const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, '0');
      const sec = String(Math.floor((diff % 60000) / 1000)).padStart(2, '0');
      const unitStyle = 'display:inline-block;text-align:center;margin:0 6px';
      const boxStyle = 'display:inline-block;width:56px;height:56px;line-height:56px;background:#1e293b;color:#fff;font-size:20px;font-weight:700;border-radius:8px;font-family:monospace';
      const labelStyle = 'display:block;font-size:10px;color:#64748b;margin-top:4px;font-weight:600;text-transform:uppercase';
      return `<div style="text-align:center;padding:${pad}">
  <span style="${unitStyle}"><span style="${boxStyle}">${d}</span><span style="${labelStyle}">${lbs.days}</span></span>
  <span style="${unitStyle}"><span style="${boxStyle}">${h}</span><span style="${labelStyle}">${lbs.hours}</span></span>
  <span style="${unitStyle}"><span style="${boxStyle}">${m}</span><span style="${labelStyle}">${lbs.minutes}</span></span>
  <span style="${unitStyle}"><span style="${boxStyle}">${sec}</span><span style="${labelStyle}">${lbs.seconds}</span></span>
</div>`;
    }

    /* ── SOCIAL ──────────────────────────── */
    case 'social': {
      type Platform = { name: string; url: string };
      const { platforms, iconSize } = el.content as { platforms: Platform[]; iconSize: number };
      const size = iconSize ?? 32;
      const colors: Record<string, string> = {
        facebook: '#1877f2', twitter: '#1da1f2', instagram: '#e1306c',
        linkedin: '#0a66c2', youtube: '#ff0000', tiktok: '#010101',
      };
      const icons = (platforms ?? []).map((p) =>
        `<a href="${p.url || '#'}" target="_blank" style="display:inline-block;margin:0 4px">
  <span style="display:inline-block;width:${size}px;height:${size}px;line-height:${size}px;
               border-radius:50%;background:${colors[p.name] ?? '#64748b'};
               color:white;text-align:center;font-size:${Math.round(size * 0.45)}px;
               font-weight:700;text-transform:uppercase;text-decoration:none">${p.name[0].toUpperCase()}</span>
</a>`
      ).join('');
      return `<div style="text-align:${align};padding:${pad}">${icons}</div>`;
    }

    /* ── MENU ────────────────────────────── */
    case 'menu': {
      type MenuItem = { label: string; href: string };
      const { items, layout } = el.content as { items: MenuItem[]; layout: string };
      const isVertical = layout === 'vertical';
      const linkStyle = `color:${(el.styles.color as string) ?? '#475569'};text-decoration:none;font-size:${(el.styles.fontSize as string) ?? '14px'};font-weight:500`;
      const links = (items ?? []).map((item) =>
        `<a href="${item.href || '#'}" style="${linkStyle}">${item.label}</a>`
      ).join(isVertical ? '<br />' : ' &nbsp;|&nbsp; ');
      return `<div style="text-align:${align};padding:${pad}">${links}</div>`;
    }

    /* ── TABLE ───────────────────────────── */
    case 'table': {
      const { headers, rows } = el.content as { headers: string[]; rows: string[][] };
      const thStyle = 'padding:10px 12px;border-bottom:2px solid #e2e8f0;text-align:left;font-weight:600;color:#0f172a;background:#f8fafc';
      const tdStyle = 'padding:10px 12px;border-bottom:1px solid #f1f5f9;color:#475569';
      const ths = (headers ?? []).map((h) => `<th style="${thStyle}">${h}</th>`).join('');
      const trs = (rows ?? []).map((row, i) => {
        const tds = row.map((cell) => `<td style="${tdStyle}">${cell}</td>`).join('');
        return `<tr style="background:${i % 2 === 0 ? '#ffffff' : '#f8fafc'}">${tds}</tr>`;
      }).join('');
      return `<table style="width:100%;border-collapse:collapse;${s}">
  <thead><tr>${ths}</tr></thead>
  <tbody>${trs}</tbody>
</table>`;
    }

    /* ── FORM ────────────────────────────── */
    case 'form': {
      type Field = { id: string; type: string; label: string; placeholder: string; required?: boolean };
      const { fields, submitText, action } = el.content as { fields: Field[]; submitText: string; action: string };
      const inputStyle = 'width:100%;padding:8px 12px;border:1px solid #e2e8f0;border-radius:6px;font-size:14px;box-sizing:border-box;margin-bottom:12px';
      const labelStyle = 'display:block;font-size:12px;font-weight:500;color:#374151;margin-bottom:4px';
      const fieldHtml = (fields ?? []).map((f) =>
        `<label style="${labelStyle}">${f.label}${f.required ? ' <span style="color:red">*</span>' : ''}</label>
<input type="${f.type}" placeholder="${f.placeholder}"${f.required ? ' required' : ''} style="${inputStyle}" />`
      ).join('');
      const btnBg = (el.styles.backgroundColor as string) ?? '#6366f1';
      return `<form action="${action ?? '#'}" method="POST" style="${s}">
  ${fieldHtml}
  <button type="submit" style="width:100%;padding:10px 16px;background:${btnBg};color:#fff;border:none;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer">${submitText ?? 'Submit'}</button>
</form>`;
    }

    /* ── COLUMNS ─────────────────────────── */
    case 'columns': {
      const { columnCount, gap } = el.content as { columnCount: number; gap: string };
      const count = columnCount ?? 2;
      const colWidth = Math.floor(100 / count);
      const cols = Array.from({ length: count }, (_, i) =>
        `<td width="${i === count - 1 ? 100 - colWidth * (count - 1) : colWidth}%" style="vertical-align:top;padding:0 ${gap ?? '8px'}"></td>`
      ).join('');
      return `<table width="100%" style="border-collapse:collapse;padding:${pad}"><tr>${cols}</tr></table>`;
    }

    /* ── CAROUSEL ────────────────────────── */
    case 'carousel': {
      type CarouselImage = { src: string; alt: string; href: string };
      const { images } = el.content as { images: CarouselImage[] };
      // Email-safe: show first image only with a link to view more
      const first = (images ?? [])[0];
      if (!first?.src) return `<!-- carousel: no images set -->`;
      const img = `<img src="${first.src}" alt="${first.alt ?? ''}" style="max-width:100%;display:block" />`;
      return `<div style="text-align:${align};padding:${pad}">
  ${first.href ? `<a href="${first.href}" target="_blank">${img}</a>` : img}
  ${images.length > 1 ? `<p style="font-size:11px;color:#94a3b8;text-align:center;margin-top:8px">${images.length} images — view in browser</p>` : ''}
</div>`;
    }

    default:
      return `<!-- ${el.type} element -->`;
  }
};

export function exportHtml(design: Design): string {
  const bodyStyles = renderStyles(design?.body?.styles as Record<string, unknown> || {});
  const maxWidth = design?.mode === 'email' ? '640px' : design?.mode === 'popup' ? '480px' : '1200px';

  const metaSubject = design?.metadata?.subject
    ? `<meta name="subject" content="${design.metadata.subject}">`
    : '';
  const metaDesc = design?.metadata?.description
    ? `<meta name="description" content="${design.metadata.description}">`
    : '';
  const preheader = design?.metadata?.preheader
    ? `<div style="display:none;max-height:0;overflow:hidden;mso-hide:all">${design.metadata.preheader}&zwnj;</div>`
    : '';

  const rows = (design?.body?.rows || []).map((row) => {
    const columns = (row.columns || []).map((col) => {
      const elements = (col.elements || [])
        .map((el) => renderElement({ type: el.type, content: el.content, styles: (el.styles || {}) as Record<string, unknown> }))
        .join('\n');
      return `<td width="${col.width || 100}%" style="vertical-align:top;padding:0">${elements}</td>`;
    }).join('\n');

    const rowStyle = renderStyles(row.styles as Record<string, unknown> || {});
    return `<tr style="${rowStyle}">\n  ${columns}\n</tr>`;
  }).join('\n');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  ${metaSubject}
  ${metaDesc}
  <title>${design?.metadata?.title ?? 'Design Studio Export'}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
    table { border-collapse: collapse; mso-table-lspace: 0; mso-table-rspace: 0; }
    img { border: 0; height: auto; line-height: 100%; outline: none; text-decoration: none; -ms-interpolation-mode: bicubic; }
    a { color: inherit; }
    @media only screen and (max-width: 600px) {
      .column { display: block !important; width: 100% !important; }
    }
  </style>
</head>
<body>
  ${preheader}
  <table role="presentation" width="100%" style="max-width:${maxWidth};margin:0 auto;${bodyStyles}">
    ${rows}
  </table>
</body>
</html>`;
}

export function exportJson(design: Design): string {
  return JSON.stringify(design, null, 2);
}
